import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Camera,
  CameraOff,
  MessageCircle,
  MessageSquare,
  Mic,
  MicOff,
  MoreVertical,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCamera } from '@/hooks/useCamera';
import {
  normalizeTranscript,
  type NormalizedTranscriptEntry,
  type TranscriptInput,
} from '../../lib/transcript';

/* ──────────────────────────────────────────
   Types
   ────────────────────────────────────────── */

export type SectionStatus =
  | 'complete'
  | 'drafting'
  | 'needs-detail'
  | 'needs-owner'
  | 'needs-links'
  | 'not-started';

export interface MeetingSection {
  id: string;
  title: string;
  status: SectionStatus;
}

export interface DraftSnippet {
  sectionTitle: string;
  text: string;
}

export interface MeetingViewProps {
  sessionName: string;
  sessionNumber?: string;
  participantName?: string;
  interviewerName?: string;
  topics?: string[];
  transcript?: TranscriptInput;
  elapsedLabel?: string;
  isPaused?: boolean;
  isMuted?: boolean;
  isEnding?: boolean;
  canPause?: boolean;
  canEnd?: boolean;
  onPauseToggle?: () => void;
  onEnd?: () => void;
  onMuteToggle?: () => void;
  error?: string | null;
}

/* ──────────────────────────────────────────
   Helpers
   ────────────────────────────────────────── */

const STATUS_CONFIG: Record<
  SectionStatus,
  { label: string; className: string; icon: string }
> = {
  complete: {
    label: 'Complete',
    className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    icon: '✓',
  },
  drafting: {
    label: 'Drafting',
    className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    icon: '●',
  },
  'needs-detail': {
    label: 'Needs detail',
    className: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    icon: '⚠',
  },
  'needs-owner': {
    label: 'Needs owner',
    className: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    icon: '⚠',
  },
  'needs-links': {
    label: 'Needs links',
    className: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    icon: '⚠',
  },
  'not-started': {
    label: 'Not started',
    className: 'bg-gray-100 text-gray-400 border-gray-200',
    icon: '○',
  },
};

function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function deriveStatusFromTranscript(
  topic: string,
  transcriptText: string,
  _allTopics: string[],
  activeIndex: number,
  topicIndex: number
): SectionStatus {
  const normalizedTopic = topic.trim().toLowerCase();
  const mentioned = transcriptText.includes(normalizedTopic);

  if (!mentioned && topicIndex > activeIndex) return 'not-started';
  if (!mentioned && topicIndex <= activeIndex) return 'not-started';

  // Basic heuristic — deeper coverage = complete, brief mention = drafting
  const words = normalizedTopic.split(/\s+/);
  const mentionCount = words.filter((w) => w.length > 3 && transcriptText.includes(w)).length;
  const coverage = words.length > 0 ? mentionCount / words.length : 0;

  if (coverage >= 0.7) return 'complete';
  if (coverage >= 0.4) return 'drafting';
  return 'needs-detail';
}

/* ──────────────────────────────────────────
   Sub-components
   ────────────────────────────────────────── */

function VideoTile({ videoRef, className }: { videoRef: React.RefObject<HTMLVideoElement>; className?: string }) {
  return (
    <video
      ref={videoRef as React.LegacyRef<HTMLVideoElement>}
      autoPlay
      playsInline
      muted
      className={cn('h-full w-full object-cover', className)}
    />
  );
}

function ParticipantTile({
  name,
  subtitle,
  initial,
  bgClass,
  videoRef,
  showVideo,
  isActive,
  showWave,
}: {
  name: string;
  subtitle?: string;
  initial: string;
  bgClass: string;
  videoRef?: React.RefObject<HTMLVideoElement>;
  showVideo?: boolean;
  isActive?: boolean;
  showWave?: boolean;
}) {
  return (
    <div className="meeting-tile group relative flex flex-col overflow-hidden rounded-xl">
      {/* Video or Avatar */}
      <div className={cn('relative flex flex-1 items-center justify-center', !showVideo && bgClass)}>
        {showVideo && videoRef ? (
          <VideoTile videoRef={videoRef} />
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 text-2xl font-bold text-white shadow-lg">
              {initial}
            </div>
            {showWave && (
              <div className="flex items-end gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={`w-${i}`}
                    className={cn(
                      'h-4 w-1 rounded-full bg-white/60',
                      'kaiva-wave',
                      i === 1 && 'kaiva-wave-delay-1',
                      i === 2 && 'kaiva-wave-delay-2',
                      i === 3 && 'kaiva-wave-delay-3'
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {/* Three dots menu (decorative) */}
        {showVideo && (
          <button
            type="button"
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-md bg-black/40 text-white/70 opacity-0 transition group-hover:opacity-100"
          >
            ⋮
          </button>
        )}
      </div>
      {/* Name bar */}
      <div className="flex items-center gap-2 bg-black/60 px-3 py-2">
        <span
          className={cn(
            'h-2 w-2 rounded-full',
            isActive ? 'bg-emerald-400' : 'bg-zinc-500'
          )}
        />
        <span className="truncate text-sm font-medium text-white">{name}</span>
        {subtitle && (
          <span className="ml-auto text-[10px] text-zinc-400">{subtitle}</span>
        )}
      </div>
    </div>
  );
}

function SidebarSectionRow({ section }: { section: MeetingSection }) {
  const config = STATUS_CONFIG[section.status];
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 transition hover:bg-gray-100">
      <div className="flex items-center gap-2.5 min-w-0">
        <span
          className={cn(
            'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
            section.status === 'complete'
              ? 'bg-emerald-500/20 text-emerald-600'
              : section.status === 'drafting'
                ? 'bg-blue-500/20 text-blue-600'
                : section.status === 'not-started'
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-amber-500/20 text-amber-600'
          )}
        >
          {config.icon}
        </span>
        <span className="truncate text-sm text-gray-700">{section.title}</span>
      </div>
      <span
        className={cn(
          'flex-shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-semibold',
          config.className
        )}
      >
        {config.label}
      </span>
    </div>
  );
}

function TranscriptMessage({ entry }: { entry: NormalizedTranscriptEntry }) {
  const isAI = entry.speaker === 'assistant';
  return (
    <div className={cn('flex gap-2.5', isAI ? 'flex-row' : 'flex-row-reverse')}>
      <div
        className={cn(
          'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold',
          isAI
            ? 'bg-[hsl(var(--kaiva-coral))]/20 text-[hsl(var(--kaiva-coral))]'
            : 'bg-[hsl(var(--kaiva-teal))]/20 text-[hsl(var(--kaiva-teal))]'
        )}
      >
        {isAI ? 'K' : 'U'}
      </div>
      <div
        className={cn(
          'max-w-[80%] rounded-xl px-3 py-2 text-sm',
          isAI
            ? 'bg-gray-100 text-gray-800'
            : 'bg-[hsl(var(--kaiva-teal))]/10 text-gray-800'
        )}
      >
        <p className="leading-relaxed">{entry.text}</p>
        <span className="mt-1 block text-[10px] text-gray-400">
          {formatTimestamp(entry.timestamp)}
        </span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────
   Toolbar button
   ────────────────────────────────────────── */

function ToolbarButton({
  icon,
  label,
  active,
  danger,
  disabled,
  onClick,
  badge,
}: {
  icon: ReactNode;
  label: string;
  active?: boolean;
  danger?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  badge?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={cn(
        'relative flex h-10 w-10 items-center justify-center rounded-lg transition',
        danger
          ? 'bg-red-600 text-white hover:bg-red-500'
          : active
            ? 'bg-gray-200 text-gray-900'
            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
        disabled && 'cursor-not-allowed opacity-40'
      )}
    >
      {icon}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
          {badge}
        </span>
      )}
    </button>
  );
}

/* ──────────────────────────────────────────
   Sidebar tab enum
   ────────────────────────────────────────── */

type SidebarTab = 'progress' | 'transcript';

/* ──────────────────────────────────────────
   Main Component
   ────────────────────────────────────────── */

export function MeetingView({
  sessionName: _sessionName,
  sessionNumber,
  participantName,
  interviewerName,
  topics,
  transcript,
  elapsedLabel,
  isPaused,
  isMuted,
  isEnding,
  canPause: _canPause,
  canEnd,
  onPauseToggle: _onPauseToggle,
  onEnd,
  onMuteToggle,
  error,
}: MeetingViewProps) {
  const camera = useCamera();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('progress');

  // Normalize transcript entries
  const transcriptEntries = useMemo(
    () => normalizeTranscript(transcript),
    [transcript]
  );

  const transcriptText = useMemo(
    () => transcriptEntries.map((e) => e.text.toLowerCase()).join(' '),
    [transcriptEntries]
  );

  // Derive section statuses from topics + transcript
  const sections: MeetingSection[] = useMemo(() => {
    if (!topics || topics.length === 0) return [];

    // Find the "active" topic index = last one that's been mentioned
    let activeIndex = -1;
    for (let i = topics.length - 1; i >= 0; i--) {
      if (transcriptText.includes(topics[i].trim().toLowerCase())) {
        activeIndex = i;
        break;
      }
    }

    return topics.map((topic, i) => ({
      id: `section-${i}`,
      title: topic,
      status: deriveStatusFromTranscript(topic, transcriptText, topics, activeIndex, i),
    }));
  }, [topics, transcriptText]);

  // Completion stats
  const completedCount = sections.filter((s) => s.status === 'complete').length;
  const draftingCount = sections.filter((s) => s.status === 'drafting').length;
  const gapCount = sections.filter(
    (s) => s.status === 'needs-detail' || s.status === 'needs-owner' || s.status === 'needs-links'
  ).length;
  const totalSections = sections.length;
  const progressPercent =
    totalSections > 0
      ? Math.round(((completedCount + draftingCount * 0.5) / totalSections) * 100)
      : 0;

  // Draft snippets from recent transcript
  const draftSnippets: DraftSnippet[] = useMemo(() => {
    const recent = transcriptEntries
      .filter((e) => e.speaker === 'user')
      .slice(-3);

    if (recent.length === 0) return [];

    // Try to match snippets to sections
    return recent.map((entry) => {
      const matchedSection = sections.find((s) =>
        entry.text.toLowerCase().includes(s.title.toLowerCase().split(' ')[0])
      );
      return {
        sectionTitle: matchedSection?.title ?? 'General',
        text: entry.text.length > 80 ? `${entry.text.slice(0, 77)}...` : entry.text,
      };
    });
  }, [transcriptEntries, sections]);

  // Attach camera stream to video element
  useEffect(() => {
    if (videoRef.current && camera.stream) {
      videoRef.current.srcObject = camera.stream;
    }
  }, [camera.stream]);

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcriptEntries.length]);

  // Current time
  const now = new Date();
  const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const participantInitial = (participantName ?? 'U').charAt(0).toUpperCase();
  const resolvedInterviewerName = interviewerName ?? 'Kaiva AI';

  return (
    <div className="meeting-container flex h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
      {/* ── Top Bar ── */}
      <header className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2">
        <div className="flex items-center gap-3">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[hsl(var(--kaiva-coral))] text-sm font-bold text-white">
            K
          </div>
          <span className="text-sm font-semibold text-gray-900">AI Interview Session</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            {timeStr}
          </span>
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* ── Error Banner ── */}
      {error && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* ── Main Content: Video Area + Sidebar ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Video Grid */}
        <div className="flex flex-1 items-center justify-center gap-4 bg-gray-100 p-4">
          {/* Kaiva AI Tile */}
          <ParticipantTile
            name={resolvedInterviewerName}
            subtitle="Host"
            initial="K"
            bgClass="meeting-tile-kaiva"
            isActive={!isPaused}
            showWave={!isPaused}
          />

          {/* Participant Tile */}
          <ParticipantTile
            name={participantName ?? 'You'}
            initial={participantInitial}
            bgClass="meeting-tile-participant"
            videoRef={videoRef}
            showVideo={camera.isActive}
            isActive
          />
        </div>

        {/* ── Sidebar ── */}
        <aside className="flex w-[380px] flex-shrink-0 flex-col border-l border-gray-200 bg-gray-50">
          {/* Sidebar Header */}
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-base font-semibold text-gray-900">Kaiva Interview</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Auto-drafting handoff · Review before publishing
            </p>
            <div className="mt-2 flex items-center gap-3">
              <span className="flex items-center gap-1.5 text-[11px] text-red-500">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                Recording
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-blue-500">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                Drafting
              </span>
              <span className="text-[11px] font-semibold text-[hsl(var(--kaiva-coral))]">
                {progressPercent}% complete
              </span>
            </div>
          </div>

          {/* Sidebar Tabs */}
          <div className="flex border-b border-gray-200">
            {(['progress', 'transcript'] as SidebarTab[]).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setSidebarTab(tab)}
                className={cn(
                  'flex-1 py-2.5 text-xs font-semibold capitalize transition',
                  sidebarTab === tab
                    ? 'border-b-2 border-[hsl(var(--kaiva-coral))] text-gray-900'
                    : 'text-gray-400 hover:text-gray-700'
                )}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Sidebar Content */}
          <div className="flex-1 overflow-y-auto">
            {/* ── Progress Tab ── */}
            {sidebarTab === 'progress' && (
              <div className="p-4">
                {/* Progress bar */}
                <div className="mb-4">
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400">
                    Interview Progress
                  </p>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500">
                    Sections complete: {completedCount}/{totalSections}
                    {gapCount > 0 && ` · Gaps: ${gapCount}`}
                  </p>
                </div>

                {/* Section list */}
                <div className="space-y-0.5">
                  {sections.map((section) => (
                    <SidebarSectionRow key={section.id} section={section} />
                  ))}
                  {sections.length === 0 && (
                    <p className="py-4 text-center text-xs text-gray-400">
                      No sections loaded
                    </p>
                  )}
                </div>

                {/* Draft Snippets */}
                {draftSnippets.length > 0 && (
                  <div className="mt-5">
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400">
                      Draft Snippets
                    </p>
                    <div className="space-y-2">
                      {draftSnippets.map((snippet, i) => (
                        <div
                          key={`snippet-${i}`}
                          className="rounded-lg border border-gray-200 bg-white p-3"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-700">
                              {snippet.sectionTitle}
                            </span>
                            <span className="rounded-md border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[9px] font-semibold text-blue-600">
                              Drafting
                            </span>
                          </div>
                          <p className="mt-1.5 text-xs leading-relaxed text-gray-500">
                            {snippet.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Transcript Tab ── */}
            {sidebarTab === 'transcript' && (
              <div className="flex flex-col gap-3 p-4">
                {transcriptEntries.length === 0 ? (
                  <p className="py-8 text-center text-xs text-gray-400">
                    Listening for the first response...
                  </p>
                ) : (
                  transcriptEntries.map((entry) => (
                    <TranscriptMessage key={entry.id} entry={entry} />
                  ))
                )}
                <div ref={transcriptEndRef} />
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* ── Bottom Toolbar ── */}
      <footer className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-2">
        {/* Left: Timer & Session ID */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">
            {elapsedLabel ?? '00:00'}
          </span>
          <span className="text-xs text-gray-300">|</span>
          <span className="text-xs text-gray-400">
            INT-{sessionNumber ?? '---'}
          </span>
        </div>

        {/* Center: Controls */}
        <div className="flex items-center gap-1.5">
          <ToolbarButton
            icon={isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            label={isMuted ? 'Unmute' : 'Mute'}
            active={!isMuted}
            onClick={onMuteToggle}
          />
          <ToolbarButton
            icon={camera.isActive ? <Camera className="h-5 w-5" /> : <CameraOff className="h-5 w-5" />}
            label={camera.isActive ? 'Turn off camera' : 'Turn on camera'}
            active={camera.isActive}
            onClick={() => void camera.toggle()}
          />
          <ToolbarButton
            icon={<MessageSquare className="h-5 w-5" />}
            label="Transcript"
            active={sidebarTab === 'transcript'}
            onClick={() => setSidebarTab(sidebarTab === 'transcript' ? 'progress' : 'transcript')}
          />
          <ToolbarButton icon={<MessageCircle className="h-5 w-5" />} label="Chat" disabled />
        </div>

        {/* Right: Leave button */}
        <button
          type="button"
          onClick={onEnd}
          disabled={!canEnd || isEnding}
          className="rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isEnding ? 'Leaving...' : 'Leave'}
        </button>
      </footer>
    </div>
  );
}

export default MeetingView;
